/**
 * FormController provides base functionality for handling form operations including
 * loading, saving, validation, and error handling. It's designed to work with OOM models
 * and provides a consistent interface for form implementations.
 *
 * @template FormModel - The OOM model type this form manages
 * @template Attributes - Initial attributes that can be passed to the form
 * @template State - Additional state properties beyond base form state
 * @template Props - Additional props beyond base form props
 */

import {
  type GetModelAttributes,
  List,
  type Model,
  type ModelConstructor,
  ValidationError,
} from "@mixitone/oom";
import { withStateToggle } from "@mixitone/util";
import { captureException } from "@mixitone/telemetry";
import MixitoneController from "controllers/MixitoneController";
import actionLogger from "lib/actionLogger";
import agentToolsService, { resource } from "services/AgentToolsService";
import type { FormFieldConfig } from "./FormFieldsDefinition";
import { batch } from "@mixitone/mvc";

/**
 * Base state interface for forms
 * @template T - The model type
 * @template R - The attributes type
 */
interface FormState<T, R> {
  id?: string;
  loading: boolean;
  saving: boolean;
  errors: Record<string, string>;
  record: T;
  newRecord: boolean;
  attributes: R;
  showDeleteModal: boolean;
}

interface FormProps<T extends Model<any>, R extends Partial<GetModelAttributes<T>>> {
  attributes: R;
  id?: string;
  onSubmit?: (record: T) => Promise<void>;
  onDelete?: (record: T) => Promise<void>;
}

@actionLogger
export class FormController<
  FormModel extends Model<any>,
  Attributes extends Partial<GetModelAttributes<FormModel>>,
  State extends {} = Record<string, unknown>,
  Props extends {} = { attributes: Attributes },
> extends MixitoneController<
  FormState<FormModel, Attributes> & State,
  FormProps<FormModel, Attributes> & Props
> {
  static override initialState: FormState<any, unknown> = {
    loading: true,
    saving: false,
    errors: {},
    record: undefined,
    attributes: {},
    newRecord: true,
    showDeleteModal: false,
  };

  protected onSubmit?: (record: FormModel) => Promise<void>;
  private _onDelete?: (record: FormModel) => Promise<void>;
  private _props!: FormProps<FormModel, Attributes> & Props;

  actionSetOnSubmit(onSubmit: (record: FormModel) => Promise<void>) {
    this.onSubmit = onSubmit;
  }

  get props() {
    return this._props;
  }

  /**
   * Define the fields for this form. Override this method to define fields.
   * @returns Record of field configurations
   */
  fields(): Record<string, FormFieldConfig> {
    // Override this method to define fields
    return {};
  }

  /**
   * Load an existing record by ID. Must be implemented by form controllers.
   * Used when editing an existing record.
   */
  findModel(_id: string): Promise<FormModel> {
    throw new Error("Not implemented");
  }

  /**
   * Create a new model instance with defaults. Must be implemented by form controllers.
   * Used when creating a new record.
   */
  async buildRecord(): Promise<FormModel> {
    throw new Error("Not implemented");
  }

  /**
   * Save the record to the backend. Must be implemented by form controllers.
   * Handle model-specific save logic here.
   */
  saveRecord(_record: FormModel): Promise<void> {
    throw new Error("Not implemented");
  }

  /**
   * Hook called after a record is loaded or built.
   * Override to load related data or perform additional setup.
   */
  async onLoad() {}

  /**
   * Hook called after a record is deleted.
   */
  async onDelete() {}

  get canDelete() {
    return this.state.record?.canDestroy() && !this.state.newRecord;
  }

  deleteMessage(): string | null {
    return null;
  }

  get canSave() {
    return true;
  }

  /** Called after a successful save (after optional `onSubmit`). Override in subclasses. */
  protected async afterSubmitSuccess(_record: FormModel): Promise<void> {}

  /**
   * Updates form state when props change.
   * Handles loading records, building new records, and calling onLoad hook.
   */
  override async changeProps(newProps: FormProps<FormModel, Attributes> & Props) {
    this._props = newProps;
    this.onSubmit = newProps.onSubmit;
    this._onDelete = newProps.onDelete;

    if (newProps.id !== this.state.id) {
      this.state.loading = true;
    }

    await batch(async () => {
      this.state.id = newProps.id;
      this.state.attributes = newProps.attributes;

      if (newProps.id) {
        if (this.state.record && this.state.record.id === newProps.id) {
          this.state.loading = false;
          return;
        }
        const record = await this.findModel(newProps.id);
        this.state.record = record;
        this.state.newRecord = false;
      } else {
        this.state.record = await this.buildRecord();
        this.state.newRecord = true;
      }

      await this.onLoad();
    });

    this.registerAgentResources();
    this.state.loading = false;
  }

  /**
   * Handles form submission.
   * - Validates and saves the record
   * - Handles validation errors and database constraints
   * - Executes onSubmit callback or navigates back on success
   * - Resets form for new records after save
   */
  @withStateToggle("saving")
  async actionSubmit() {
    if (!this.canSave) return;

    try {
      await this.saveRecord(this.state.record);

      if (this.onSubmit) {
        await this.onSubmit(this.state.record);
      } else {
        this.actionBack();
      }

      await this.afterSubmitSuccess(this.state.record);

      // For a new record reset the form after save
      if (this.state.newRecord) {
        this.state.record = await this.buildRecord();
      }
    } catch (err) {
      captureException(err);
      this.state.errors = {};

      console.error(err);

      if (err instanceof ValidationError) {
        for (const [key, value] of Object.entries(err.errors)) {
          this.state.errors[key] = value;
        }
      }

      if (err instanceof Error) {
        const { message } = err;
        if (message.includes("null value") && message.includes("violates not-null constraint")) {
          const match = message.match(/column "(.*?)"/);
          if (match?.[1]) {
            this.state.errors[match[1]] = "cannot be blank";
          }
        }
      }
    }
  }

  /**
   * Shows the delete confirmation modal
   */
  async actionDelete() {
    this.state.showDeleteModal = true;
  }

  /**
   * Hides the delete confirmation modal
   */
  async actionCancelDelete() {
    this.state.showDeleteModal = false;
  }

  /**
   * Handles record deletion after confirmation
   * - Deletes the record
   * - Executes onDelete callback or navigates back
   * - Handles errors with Sentry integration
   */
  @withStateToggle("saving")
  async actionConfirmDelete() {
    try {
      if (this.state.newRecord) throw new Error("Cannot delete a new record");
      await this.state.record.destroy();
      await this.afterDestroy();
    } catch (err) {
      console.error(err);
      captureException(err);
    } finally {
      this.state.showDeleteModal = false;
    }
  }

  /**
   * Handles form cancellation
   * - Reloads existing record data
   * - Executes onSubmit callback or navigates back
   */
  actionCancel() {
    if (!this.state.newRecord) {
      this.state.record.reload();
    }
    if (this.onSubmit) {
      this.onSubmit(this.state.record);
    } else {
      this.actionBack();
    }
  }

  /**
   * Called after successful record deletion
   * Executes onDelete callback or navigates back
   */
  async afterDestroy() {
    console.log("After destroy called");
    await this.onDelete();

    if (this._onDelete) {
      this._onDelete(this.state.record);
    } else {
      this.actionBack();
    }
  }

  protected registerAgentResources() {
    this.addDependency(
      agentToolsService.addResources(this, {
        context: () => {
          const fields = this.fields();
          return `The user is viewing a form for a record in table ${this.state.record?.tableName}. 
            The record is ${this.state.record?.id}${"name" in this.state.record ? ` with name ${this.state.record.name}` : ""}.
            ${this.state.newRecord ? "This is a new record." : "This is an existing record."}
          ${fields && Object.keys(fields).length > 0 ? `The form may have the following fields (although grouping can toggle when these are shown): ${JSON.stringify(fields)}` : ""}`;
        },
        definitions: {
          [this.state.record?.tableName]: resource(
            this.state.record?.tableName,
            this.state.record?.constructor as ModelConstructor<FormModel>,
            {
              list: () => (this.state.record ? new List(this.state.record) : new List()),
            },
          ),
        },
      }),
    );
  }
}
